import { isExpandAlreadyPending, type PendingTurnFrame, type PendingUserMessage } from './pendingTurnQueue';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';

/**
 * The outbound queue a tab should hold after a newer tab supersedes it.
 *
 * The turn the user just sent was rejected server-side before it persisted (the
 * eviction pre-check runs ahead of processing), so it would otherwise vanish.
 * Both halves of the in-flight turn are put back at the FRONT of the queue, so
 * an explicit reclaim re-sends them before anything queued behind them.
 *
 * The two halves are recovered differently because only one of them left a
 * trace. A user message has an optimistic bubble still on screen to read back;
 * an expand's only trace would have been the server's card, which supersession
 * means never arrived — so its id has to be passed in.
 *
 * @param liveMessages - the transcript as rendered, source of the in-flight bubbles.
 * @param queue - the outbound queue as it stands, oldest first.
 * @param supersededExpandId - the alert the rejected turn was expanding, or `null`.
 * @returns the new queue. Entries already present are not duplicated, so a
 * re-entrant close cannot queue the same turn twice.
 */
export function requeueSupersededTurn(
  liveMessages: readonly IConversationMessage[],
  queue: readonly PendingTurnFrame[],
  supersededExpandId: string | null
): PendingTurnFrame[] {
  const alreadyQueuedIds = new Set(queue.flatMap((entry) => (entry.kind === 'user' ? [entry.clientMessageId] : [])));

  const requeuedUserMessages = liveMessages
    .filter(
      (message) =>
        message.role === 'user' &&
        message.isPending === true &&
        message.isInFlight === true &&
        message.clientMessageId !== undefined &&
        !alreadyQueuedIds.has(message.clientMessageId)
    )
    .map((message): PendingUserMessage => {
      const attachmentDocumentIds = message.attachments?.map((attachment) => attachment.id) ?? [];
      return {
        kind: 'user',
        content: message.content,
        clientMessageId: message.clientMessageId as string,
        ...(attachmentDocumentIds.length > 0 ? { attachmentDocumentIds } : {}),
      };
    });

  const withUserMessages = [...requeuedUserMessages, ...queue];

  if (supersededExpandId === null || isExpandAlreadyPending(supersededExpandId, null, withUserMessages)) {
    return withUserMessages;
  }
  return [{ kind: 'alert-expand', alertId: supersededExpandId }, ...withUserMessages];
}
